import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import { 
  getTaskAutomationRules, 
  deleteTaskAutomationRule,
  deleteAutomationRule,
  getDropdownData,
  toggleAutomationRule
} from '../services/automationApi';
import { TaskAutomationRule, DropdownData } from '../types/Automation';
import TaskAutomationForm from './TaskAutomationForm';
import TaskAutomationTestModal from './TaskAutomationTestModal';
import { useBranch } from '../contexts/BranchContext';
import PermissionGate from './PermissionGate';

const TaskAutomationManager: React.FC = () => {
  const { currentBranch, isViewingAllBranches, loading: branchLoading } = useBranch();
  const [rules, setRules] = useState<TaskAutomationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<TaskAutomationRule | null>(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testRule, setTestRule] = useState<TaskAutomationRule | null>(null);
  const [dropdownData, setDropdownData] = useState<DropdownData | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Load data
  const loadRules = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getTaskAutomationRules({ 
        page, 
        limit: 20,
        sortBy: 'created_at',
        sortOrder: 'DESC'
      });
      
      
      // Ensure we have valid data
      setRules(response.rules || []);
      setPagination(response.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        hasNextPage: false,
        hasPrevPage: false
      });
    } catch (err: any) {
      console.error('Error loading rules:', err);
      setError(err.message || 'Failed to load automation rules');
      setRules([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        hasNextPage: false,
        hasPrevPage: false
      });
    } finally {
      setLoading(false);
    }
  };

  const loadDropdownData = async () => {
    try {
      const data = await getDropdownData();
      setDropdownData(data);
    } catch (err) {
      console.error('Failed to load dropdown data:', err);
    }
  };

  const handleToggleRule = async (ruleId: number) => {
    try {
      const result = await toggleAutomationRule(ruleId);
      
      // Update the local state immediately for better UX
      setRules(prevRules => 
        prevRules.map(rule => 
          rule.automationRuleId === ruleId 
            ? { ...rule, automationRuleActive: result.isActive }
            : rule
        )
      );
      
      // Also reload rules to ensure data consistency
      await loadRules();
    } catch (err) {
      console.error('Failed to toggle rule:', err);
      setError('Failed to toggle rule status');
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  useEffect(() => {
    if (!branchLoading) {
      loadRules();
      loadDropdownData();
    }
  }, [currentBranch, isViewingAllBranches, branchLoading]);

  const handleCreateRule = () => {
    setEditingRule(null);
    setShowForm(true);
  };

  const handleEditRule = (rule: TaskAutomationRule) => {
    setEditingRule(rule);
    setShowForm(true);
  };

  const handleDeleteRule = async (rule: TaskAutomationRule) => {
    if (!window.confirm(`Are you sure you want to delete the automation rule "${rule.taskName}"?`)) {
      return;
    }

    try {
      // Delete the main automation rule (this will cascade delete the task automation rule)
      await deleteAutomationRule(rule.automationRuleId);
      await loadRules(pagination.currentPage);
    } catch (err: any) {
      setError(err.message || 'Failed to delete automation rule');
    }
  };

  const handleTestRule = (rule: TaskAutomationRule) => {
    setTestRule(rule);
    setShowTestModal(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingRule(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingRule(null);
    loadRules(pagination.currentPage);
  };

  const handleTestModalClose = () => {
    setShowTestModal(false);
    setTestRule(null);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (isActive?: boolean) => {
    if (isActive === undefined) return null;
    return isActive ? (
      <CheckCircleIcon className="h-5 w-5 text-green-500" />
    ) : (
      <XCircleIcon className="h-5 w-5 text-red-500" />
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Task Automation Rules</h2>
          <p className="text-sm text-gray-600">
            Automatically create tasks when leads move to specific stages
          </p>
        </div>
        <PermissionGate module="automation" permission="create">
          <button
            onClick={handleCreateRule}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            New Rule
          </button>
        </PermissionGate>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XCircleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Rules List */}
      {!rules || rules.length === 0 ? (
        <div className="text-center py-12">
          <Cog6ToothIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No automation rules</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating your first task automation rule.
          </p>
          <div className="mt-6">
            <PermissionGate module="automation" permission="create">
              <button
                onClick={handleCreateRule}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Rule
              </button>
            </PermissionGate>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {rules && rules.map((rule) => (
            <div key={rule.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-lg font-medium text-gray-900">{rule.taskName}</h3>
                      {getStatusIcon(rule.automationRuleActive)}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(rule.automationRuleActive || false)}`}>
                        {rule.automationRuleActive ? 'Active' : 'Inactive'}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(rule.priority)}`}>
                        {rule.priority}
                      </span>
                      {rule.requiresPermission && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Permission Required
                        </span>
                      )}
                    </div>
                    
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Trigger:</span> When lead stage is "{rule.stageName}"
                    </div>
                    <div>
                      <span className="font-medium">Category:</span> {rule.categoryName || 'Not set'}
                    </div>
                    <div>
                      <span className="font-medium">Assignment Mode:</span> 
                      <span className={`ml-1 px-2 py-0.5 rounded text-xs font-medium ${
                        rule.assignmentMode === 'assign_all' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {rule.assignmentMode === 'assign_all' ? 'Assign All' : 'Divide (Rotate)'}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Assignees:</span> {
                        rule.assigneeIds && rule.assigneeIds.length > 0 
                          ? `${rule.assigneeIds.length} selected` 
                          : rule.assigneeName || 'Not assigned'
                      }
                    </div>
                    <div>
                      <span className="font-medium">Supervisors:</span> {
                        rule.supervisorIds && rule.supervisorIds.length > 0 
                          ? `${rule.supervisorIds.length} selected` 
                          : rule.supervisorName || 'Not assigned'
                      }
                    </div>
                    <div>
                      <span className="font-medium">Due Date:</span> {rule.dueDateOffset} days after trigger
                    </div>
                    <div>
                      <span className="font-medium">Reminder:</span> {rule.reminderOffset ? `${rule.reminderOffset} hours before` : 'No reminder'}
                    </div>
                    {rule.assignmentMode === 'divide' && (
                      <>
                        <div>
                          <span className="font-medium">Rotation Interval:</span> Every {rule.rotationInterval} lead change(s)
                        </div>
                        <div>
                          <span className="font-medium">Total Rotations:</span> {rule.totalRotations || 0}
                        </div>
                        <div>
                          <span className="font-medium">Last Rotation:</span> {
                            rule.lastRotationDate 
                              ? new Date(rule.lastRotationDate).toLocaleDateString()
                              : 'Never'
                          }
                        </div>
                      </>
                    )}
                  </div>

                  {rule.taskDescription && (
                    <div className="mt-3">
                      <span className="font-medium text-gray-700">Description:</span>
                      <p className="text-sm text-gray-600 mt-1">{rule.taskDescription}</p>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <PermissionGate module="automation" permission="create">
                    <button
                      onClick={() => handleTestRule(rule)}
                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Test Rule"
                    >
                      <PlayIcon className="h-4 w-4" />
                    </button>
                  </PermissionGate>
                  <PermissionGate module="automation" permission="edit">
                    <button
                      onClick={() => handleEditRule(rule)}
                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Edit Rule"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </PermissionGate>
                  <PermissionGate module="automation" permission="delete">
                    <button
                      onClick={() => handleDeleteRule(rule)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete Rule"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {((pagination?.currentPage - 1) * 20) + 1} to {Math.min((pagination?.currentPage || 1) * 20, pagination?.totalRecords || 0)} of {pagination?.totalRecords || 0} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => loadRules(pagination?.currentPage - 1)}
              disabled={!pagination?.hasPrevPage}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => loadRules(pagination?.currentPage + 1)}
              disabled={!pagination?.hasNextPage}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Forms and Modals */}
      {showForm && (
        <TaskAutomationForm
          rule={editingRule}
          dropdownData={dropdownData}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}

      {showTestModal && testRule && (
        <TaskAutomationTestModal
          rule={testRule}
          onClose={handleTestModalClose}
        />
      )}
    </div>
  );
};

export default TaskAutomationManager;
